

import { DynamicFieldData } from '@/shared/components/DynamicForm/models/DynamicFieldData'
import { AdditionalTab, DynamicFormDefinition, DynamicFormType } from '@/shared/components/DynamicForm/models/DynamicFormSection'

import { FormFieldDefaultValue, FormFieldSelectOptions, getDynamicFormFields } from '../../shared/lib/getDynamicFormFields'

const defaultOrderDetailFields: DynamicFieldData[] = [
  {
    fieldType: 'section',
    fieldName: 'orderDetailSectionTitle',
    label: 'Order Information',
    showDivider: false,
  },
  {
    fieldType: 'text',
    fieldName: 'orderNumber',
    label: 'Order Number',
    placeholder: 'Order Number',
    defaultValue: '',
    required: true,
    config: {
      required: 'Order number is required',
    },
  },
  {
    fieldType: 'select',
    fieldName: 'depot',
    label: 'Depot',
    placeholder: 'Select Depot',
    required: true,
    options: [],
    config: {
      required: 'Depot is required',
    },
  },
  {
    fieldType: 'text',
    fieldName: 'consignmentNumber',
    label: 'Consignment Number',
    placeholder: 'Consignment Number',
    defaultValue: '',
    config: {},
  },
  {
    fieldType: 'radiogroup',
    fieldName: 'isPickup',
    label: 'Type',
    defaultValue: '0', // '0' for Delivery, '1' for Pickup
    options: [
      { label: 'Delivery', value: '0' },
      { label: 'Pickup', value: '1' },
    ],
    row: true,
  },
  {
    fieldType: 'date',
    fieldName: 'preferredDateOfOperation',
    label: 'Date',
    defaultValue: null,
    config: {},
  },
  {
    fieldName: 'attributes',
    fieldType: 'multiselect',
    label: 'Attributes',
    placeholder: 'Select Order Attributes',
    options: [],
    defaultValue: [],
    config: {},
  },
  {
    fieldType: 'text',
    fieldName: 'tripNumber',
    label: 'Trip Number',
    placeholder: 'Trip Number',
    defaultValue: '',
    config: {},
  },
  {
    fieldType: 'textarea',
    fieldName: 'description',
    label: 'Description',
    placeholder: 'Order Description',
    defaultValue: '',
    required: false,
    config: {
      maxLength: {
        value: 400,
        message: 'Description cannot exceed 400 characters',
      },
    },
  },
  {
    fieldType: 'textarea',
    fieldName: 'deliveryInstruction',
    label: 'Delivery Instructions',
    placeholder: 'Delivery Instructions',
    defaultValue: '',
    required: false,
    config: {
      maxLength: {
        value: 400,
        message: 'Description cannot exceed 400 characters',
      },
    },
  },
  {
    fieldType: 'textarea',
    fieldName: 'comment',
    label: 'Comments',
    placeholder: 'Comments',
    defaultValue: '',
    config: {
      maxLength: {
        value: 450,
        message: 'Comments cannot exceed 450 characters',
      },
    },
  },
  {
    fieldType: 'section',
    fieldName: 'orderItemsSectionTitle',
    label: '',
    showDivider: true,
  },
  {
    fieldType: 'orderItems',
    fieldName: 'orderItems',
    label: 'Order Items',
    defaultValue: [],
  },
  {
    fieldType: 'number',
    fieldName: 'weight',
    label: 'Total Weight',
    placeholder: 'Weight',
    defaultValue: 0,
    config: {
      min: {
        value: 0,
        message: 'Weight cannot be less than 0',
      },
    },
    grid: {
      xs: 12,
      sm: 4,
    },
  },
  {
    fieldType: 'number',
    fieldName: 'volume',
    label: 'Total Volume',
    placeholder: 'Volume',
    defaultValue: 0,
    config: {
      min: {
        value: 0,
        message: 'Volume cannot be less than 0',
      },
    },
    grid: {
      xs: 12,
      sm: 4,
    },
  },
  {
    fieldType: 'number',
    fieldName: 'quantity',
    label: 'Total Quantity',
    placeholder: 'Quantity',
    defaultValue: 0,
    config: {
      min: {
        value: 0,
        message: 'Quantity cannot be less than 0',
      },
    },
    grid: {
      xs: 12,
      sm: 4,
    },
  },
]

const defaultCustomerDetailFields: DynamicFieldData[] =  [
  {
    fieldType: 'section',
    fieldName: 'customerInfoSectionTitle',
    label: 'Customer Info',
    showDivider: false,
  },
  {
    fieldType: 'text',
    fieldName: 'address.name',
    label: 'Name',
    placeholder: 'Customer Name',
    defaultValue: '',
    config: {},
  },
  {
    fieldType: 'text',
    fieldName: 'address.externalId',
    label: 'Customer Ref',
    placeholder: 'Customer Reference Number',
    defaultValue: '',
    config: {},
  },
  {
    fieldType: 'text',
    fieldName: 'address.contact',
    label: 'Contact',
    placeholder: 'Contact',
    defaultValue: '',
    config: {},
  },
  {
    fieldType: 'text',
    fieldName: 'emailPickup',
    label: 'Email',
    placeholder: 'Email',
    defaultValue: '',
    dependencies: [
      {
        fieldName: 'isPickup',
        values: ['1'],
        operator: '==',
      },
    ],
    config: {
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address.',
      },
    },
  },
  {
    fieldType: 'text',
    fieldName: 'emailDelivery',
    label: 'Email',
    placeholder: 'Email',
    defaultValue: '',
    dependencies: [
      {
        fieldName: 'isPickup',
        values: ['0'],
        operator: '==',
      },
    ],
    config: {
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address.',
      },
    },
  },
  {
    fieldType: 'text',
    fieldName: 'phonePickup',
    label: 'Phone No.',
    placeholder: 'Phone Number',
    defaultValue: '',
    dependencies: [
      {
        fieldName: 'isPickup',
        values: ['1'],
        operator: '==',
      },
    ],
    config: {},
  },
  {
    fieldType: 'text',
    fieldName: 'phoneDelivery',
    label: 'Phone No.',
    placeholder: 'Phone Number',
    defaultValue: '',
    dependencies: [
      {
        fieldName: 'isPickup',
        values: ['0'],
        operator: '==',
      },
    ],
    config: {},
  },
  {
    fieldType: 'text',
    fieldName: 'smsPickup',
    label: 'SMS No.',
    placeholder: 'SMS Number',
    defaultValue: '',
    dependencies: [
      {
        fieldName: 'isPickup',
        values: ['1'],
        operator: '==',
      },
    ],
    config: {},
  },
  {
    fieldType: 'text',
    fieldName: 'smsDelivery',
    label: 'SMS No.',
    placeholder: 'SMS Number',
    defaultValue: '',
    dependencies: [
      {
        fieldName: 'isPickup',
        values: ['0'],
        operator: '==',
      },
    ],
    config: {},
  },
  {
    fieldType: 'section',
    fieldName: 'customerAddressSectionTitle',
    label: 'Address',
    showDivider: false,
  },
  {
    fieldType: 'address',
    fieldName: 'address',
    label: 'Address',
    defaultValue: {
      street: '',
      suburb: '',
      state: '',
      postcode: '',
      country: 'Australia',
    },
  },
]

const defaultSchedulingDetailFields: DynamicFieldData[] = [
  {
    fieldType: 'section',
    fieldName: 'schedulingSectionTitle',
    label: 'Scheduling Detail',
    showDivider: false,
  },
  {
    fieldType: 'time',
    fieldName: 'serviceTime',
    label: 'Site Service Time',
    defaultValue: '',
    views: ['hours', 'minutes', 'seconds'],
    format: 'HH:mm:ss',
  },
  {
    fieldType: 'time',
    fieldName: 'maxOnboardTime',
    label: 'Max Onboard Time',
    defaultValue: '',
    views: ['hours', 'minutes'],
    format: 'HH:mm',
  },
  {
    fieldType: 'time',
    fieldName: 'twStart',
    label: 'TW Start',
    defaultValue: '',
    views: ['hours', 'minutes'],
    format: 'HH:mm',
    config: {},
    grid: {
      xs: 12,
      sm: 10,
    },
  },
  {
    fieldType: 'checkbox',
    fieldName: 'twStartNextDay',
    label: 'ND',
    addTopLabelMargin: true,
    defaultValue: false,
    config: {},
    grid: {
      xs: 12,
      sm: 2,
    },
  },
  {
    fieldType: 'time',
    fieldName: 'twFinish',
    label: 'TW Finish',
    defaultValue: '',
    views: ['hours', 'minutes'],
    format: 'HH:mm',
    config: {},
    grid: {
      xs: 12,
      sm: 10,
    },
  },
  {
    fieldType: 'checkbox',
    fieldName: 'twFinishNextDay',
    label: 'ND',
    addTopLabelMargin: true,
    defaultValue: false,
    config: {},
    grid: {
      xs: 12,
      sm: 2,
    },
  },
  {
    fieldType: 'text',
    fieldName: 'priority',
    label: 'Priority',
    placeholder: 'Order Priority',
    defaultValue: '',
    config: {},
  },
]

type GetFormDefinitionForDepotModelArgs = {
  defaultValues?: FormFieldDefaultValue
  selectOptions?: FormFieldSelectOptions
  additionalTabs?: AdditionalTab[]
  formType?: DynamicFormType
}

export const getFormDefinitionForDepotModel = ({
  defaultValues, 
  selectOptions,
  additionalTabs,
  formType = 'stepper',
}: GetFormDefinitionForDepotModelArgs): DynamicFormDefinition => {
  const orderDetailFields = getDynamicFormFields({
    fields: defaultOrderDetailFields,
    defaultValues,
    selectOptions,
  })
  const customerDetailFields = getDynamicFormFields({
    fields: defaultCustomerDetailFields,
    defaultValues,
    selectOptions,
  })
  const schedulingDetailFields = getDynamicFormFields({
    fields: defaultSchedulingDetailFields,
    defaultValues,
    selectOptions,
  })

  const formDefinition: DynamicFormDefinition = {
    formType: formType,
    additionalTabs: additionalTabs,
    sections: [
      {
        label: 'Order Detail',
        fields: orderDetailFields,
      },
      {
        label: 'Customer Detail',
        fields: customerDetailFields,
      },
      {
        label: 'Scheduling Detail',
        fields: schedulingDetailFields,
      },
    ],
  }

  return formDefinition
}